import { useEffect, useState } from 'react';
import type { ConnectionState, RealtimeEvent, RealtimeEventType } from 'shared';
import { getToken } from './api';

type Handler = (event: RealtimeEvent) => void;

const handlers = new Map<RealtimeEventType, Set<Handler>>();
const stateListeners = new Set<(s: ConnectionState) => void>();

let socket: WebSocket | null = null;
let attempt = 0;
let reconnectTimer: number | null = null;
let state: ConnectionState = 'offline';

/** На сколько часы сервера впереди наших. Уточняется каждым пингом. */
let offset = 0;

/**
 * Текущее время по часам сервера.
 *
 * Для торгов это не педантизм: срок ставок неподвижен, но сверяет с ним
 * сервер, а часы на телефоне уходят на минуты. Таймер по местным часам
 * либо гасит форму раньше времени, либо держит её открытой после срока.
 */
export function serverNow(): number {
  return Date.now() + offset;
}

/** Согласовать часы по ответу сервера, когда пинга ещё не было. */
export function syncClock(serverTs: number) {
  offset = serverTs - Date.now();
}

function setState(next: ConnectionState) {
  if (state === next) return;
  state = next;
  for (const l of stateListeners) l(next);
}

function url(): string | null {
  const token = getToken();
  if (!token) return null;

  const base = import.meta.env.VITE_API_URL ?? '';

  // VITE_API_URL — либо относительный путь (/api), либо полный адрес.
  // В первом случае берём хост страницы и подменяем схему на ws/wss.
  const httpUrl = base.startsWith('http')
    ? `${base}/ws`
    : `${window.location.origin}${base}/ws`;

  const u = new URL(httpUrl);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.searchParams.set('token', token);
  return u.toString();
}

function scheduleReconnect() {
  if (reconnectTimer !== null) return;

  // 1, 2, 4… до 30 секунд. Мгновенный повтор в цикле только добьёт
  // сервер, если он и так лежит.
  const delay = Math.min(1000 * 2 ** attempt, 30_000);
  attempt += 1;

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

export function connect() {
  const target = url();
  if (!target) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  setState('connecting');

  try {
    socket = new WebSocket(target);
  } catch {
    setState('offline');
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    attempt = 0;
    setState('online');
  };

  socket.onmessage = (e) => {
    let event: RealtimeEvent;
    try {
      event = JSON.parse(e.data as string) as RealtimeEvent;
    } catch {
      return;
    }
    if (event.type === 'ping') {
      // Часы телефона врут на минуты, а обратный отсчёт торгов обязан
      // совпадать с сервером до секунды: иначе человек увидит ноль там,
      // где ставки ещё принимают. Пинг приходит раз в 30 секунд и несёт
      // серверное время — этого достаточно, отдельного запроса не нужно.
      offset = event.ts - Date.now();
      return;
    }

    for (const h of handlers.get(event.type) ?? []) {
      try {
        h(event);
      } catch {
        // Ошибка одного подписчика не должна ронять остальных.
      }
    }
  };

  socket.onclose = (e) => {
    setState('offline');
    socket = null;
    // 4001/4003 — токен недействителен или доступ закрыт. Переподключаться
    // с тем же токеном бессмысленно, ждём нового входа.
    if (e.code !== 4001 && e.code !== 4003) scheduleReconnect();
  };

  socket.onerror = () => {
    // onclose придёт следом и займётся переподключением.
  };
}

export function disconnect() {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  attempt = 0;
  socket?.close();
  socket = null;
  setState('offline');
}

/** Подписка на тип события. Возвращает функцию отписки. */
export function on<T extends RealtimeEventType>(
  type: T,
  handler: (event: Extract<RealtimeEvent, { type: T }>) => void,
): () => void {
  let bucket = handlers.get(type);
  if (!bucket) {
    bucket = new Set();
    handlers.set(type, bucket);
  }

  const wrapped = handler as Handler;
  bucket.add(wrapped);

  return () => {
    bucket.delete(wrapped);
    if (bucket.size === 0) handlers.delete(type);
  };
}

/** Реагируем на события в компоненте. */
export function useRealtime<T extends RealtimeEventType>(
  type: T,
  handler: (event: Extract<RealtimeEvent, { type: T }>) => void,
  deps: unknown[] = [],
) {
  useEffect(() => {
    return on(type, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useConnectionState(): ConnectionState {
  const [value, setValue] = useState<ConnectionState>(state);

  useEffect(() => {
    stateListeners.add(setValue);
    return () => {
      stateListeners.delete(setValue);
    };
  }, []);

  return value;
}

// Вкладку свернули и вернули — соединение могло умереть незаметно.
// Проверяем при каждом возвращении фокуса и при появлении сети.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') connect();
  });
  window.addEventListener('online', () => {
    attempt = 0;
    connect();
  });
}


/**
 * Страховка на случай, когда события до нас не дошли.
 *
 * Сокет — не гарантия доставки: соединение могло висеть «открытым» при
 * уснувшем ноутбуке, событие могло родиться в обход API (скрипт наполнения,
 * ручная правка в базе), вкладка могла провисеть в фоне полчаса. Поэтому
 * экраны со списками ещё и перечитывают данные — при возврате фокуса и
 * раз в минуту, пока вкладка видима.
 *
 * Без этого пользователь смотрит на устаревший список и не понимает,
 * почему у соседа заявки есть, а у него нет.
 */
export function useLiveRefresh(reload: () => void, intervalMs = 60_000) {
  useEffect(() => {
    let last = Date.now();

    const run = () => {
      // Защита от частых срабатываний: возврат фокуса и таймер легко
      // приходят подряд.
      if (Date.now() - last < 5000) return;
      last = Date.now();
      reload();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', run);
    window.addEventListener('online', run);

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, intervalMs);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', run);
      window.removeEventListener('online', run);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
}
